import { openDB, unwrap } from 'idb'
import { normalizeDocument } from '../web-canvas/document.js'

export const DB_NAME = 'blaze-web-canvas'
// v6 adds the gen_jobs recovery indexes (canvas/status/client-job scans).
// Upgrades remain additive: no existing store or row is deleted.
export const DB_VERSION = 6

export const STORE_DEFINITIONS = Object.freeze([
  ['canvas_working_copies', { keyPath: 'id' }],
  ['canvas_view_state', { keyPath: 'canvasId' }],
  ['sync_outbox', { keyPath: 'canvasId' }],
  ['asset_uploads', { keyPath: 'hash' }],
  ['auth_ui_state', undefined],
  ['gen_jobs', { keyPath: 'id' }],
  // v4: resolved remote-media capabilities (presigned URL + variants) survive
  // reloads so re-entering the canvas list doesn't pay a gateway presign
  // round trip while the URLs are still fresh.
  ['remote_media_cache', { keyPath: 'hash' }],
])

export const STORE_INDEX_DEFINITIONS = Object.freeze({
  gen_jobs: Object.freeze([
    ['by_canvas', 'canvasId'],
    ['by_status', 'status'],
    ['by_client_job', 'clientJobId', { unique: true }],
  ]),
})

function ensureIndexes(store, definitions = []) {
  if (!store?.createIndex) return
  for (const [name, keyPath, options] of definitions) {
    if (!store.indexNames?.contains?.(name)) store.createIndex(name, keyPath, options)
  }
}

export function ensureObjectStores(db, transaction = null) {
  for (const [name, options] of STORE_DEFINITIONS) {
    const store = db.objectStoreNames.contains(name)
      ? transaction?.objectStore?.(name)
      : db.createObjectStore(name, options)
    ensureIndexes(store, STORE_INDEX_DEFINITIONS[name])
  }
}

function cursorEach(store, visit) {
  // idb wraps openCursor() as a Promise, but an upgrade backfill must enqueue
  // raw IDBRequests synchronously on the active versionchange transaction.
  // Keep the fallback for the lightweight stores used by unit tests.
  const cursorStore = unwrap(store) || store
  if (!cursorStore?.openCursor) return
  const request = cursorStore.openCursor()
  request.onsuccess = () => {
    const cursor = request.result
    if (!cursor) return
    visit(cursor.value)
    cursor.continue()
  }
}

function hashFromAssetUri(uri) {
  return /^asset:\/\/([0-9a-f]{16})\./i.exec(String(uri || ''))?.[1]?.toLowerCase() || null
}

let databasePromise = null

export function getDatabase() {
  if (!databasePromise) {
    databasePromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db, _oldVersion, _newVersion, transaction) {
        ensureObjectStores(db, transaction)
      },
    })
  }
  return databasePromise
}

export async function readStore(store, key) { return (await getDatabase()).get(store, key) }
export async function readAllStore(store) { return (await getDatabase()).getAll(store) }
export async function readStoreIndex(store, index, key) { return (await getDatabase()).getFromIndex(store, index, key) }
export async function writeStore(store, value, key) {
  const db = await getDatabase()
  return key === undefined ? db.put(store, value) : db.put(store, value, key)
}
export async function deleteStore(store, key) { return (await getDatabase()).delete(store, key) }

function nextRevision(row) {
  return Math.max(0, Number(row?.localRevision) || 0) + 1
}

const assetHashFromUri = hashFromAssetUri

function generationItemIds(input, job) {
  for (const values of [input?.itemIds, job?.itemIds]) {
    if (!Array.isArray(values)) continue
    const normalized = [...new Set(values.map(value => String(value || '').trim()).filter(Boolean))]
    if (normalized.length) return normalized
  }
  const singular = input?.resultItemId || job?.resultItemId || null
  return singular ? [String(singular)] : []
}

function generationImportedAssets(input, job, itemIds) {
  const source = Array.isArray(input?.importedAssets) && input.importedAssets.length
    ? input.importedAssets
    : (Array.isArray(job?.importedAssets) && job.importedAssets.length
        ? job.importedAssets
        : (job?.importedUri || input?.assetRecord || job?.importedAssetRow
            ? [{
                ordinal: 0, itemId: itemIds[0] || job?.resultItemId || null,
                uri: job?.importedUri || null,
                assetRow: input?.assetRecord || job?.importedAssetRow || null,
              }]
            : []))
  const records = Array.isArray(input?.assetRecords) ? input.assetRecords : []
  const byHash = new Map(records.filter(Boolean).map(record => [String(record.hash || '').toLowerCase(), record]))
  return source
    .map((asset, index) => {
      const ordinal = Number.isSafeInteger(asset?.ordinal) ? asset.ordinal : index
      const uri = asset?.uri || (ordinal === 0 ? job?.importedUri : null)
      const hash = assetHashFromUri(uri)
      return {
        ...asset,
        ordinal,
        itemId: asset?.itemId || itemIds[index] || null,
        uri,
        assetRow: asset?.assetRow || byHash.get(hash) || (ordinal === 0 ? input?.assetRecord || job?.importedAssetRow : null),
      }
    })
    .sort((a, b) => a.ordinal - b.ordinal)
}

// The paid-result durability boundary. Canvas bytes, cloud outbox intent, the
// local job state, and the independent task projection commit together in one
// readwrite transaction. Rendering the result is not delivery until this
// transaction resolves.
export async function commitGenerationDelivery(input, database = null) {
  const db = database || await getDatabase()
  const tx = db.transaction(
    ['asset_uploads', 'canvas_working_copies', 'sync_outbox', 'gen_jobs'],
    'readwrite',
  )
  const workingStore = tx.objectStore('canvas_working_copies')
  const uploadStore = tx.objectStore('asset_uploads')
  const outboxStore = tx.objectStore('sync_outbox')
  const jobsStore = tx.objectStore('gen_jobs')
  const [working, outbox, job] = await Promise.all([
    workingStore.get(input.canvasId),
    outboxStore.get(input.canvasId),
    jobsStore.get(input.jobId),
  ])
  // The repository performs an early guard too, but a Realtime delete can land
  // between that read and this transaction. Re-check both durable deletion
  // representations inside the same transaction that would place the paid
  // result, otherwise the delivery could resurrect a canvas after deletion.
  if (working?.deleted || outbox?.deleted) {
    throw Object.assign(new Error('canvas_deleted'), {
      code: 'canvas_deleted',
      canvasId: input.canvasId,
    })
  }
  if (!job) throw Object.assign(new Error('generation_job_not_found'), { code: 'generation_job_not_found' })
  if (!['imported', 'delivered_local'].includes(job.status)) {
    throw Object.assign(new Error('generation_not_imported'), { code: 'generation_not_imported' })
  }

  const now = Number(input.now) || Date.now()
  const clientJobId = input.clientJobId || job.clientJobId || job.id
  const deliveryId = input.deliveryId || job.deliveryId
  if (!deliveryId) throw Object.assign(new Error('generation_delivery_id_required'), { code: 'generation_delivery_id_required' })
  if (job.status === 'delivered_local') {
    if (job.deliveryId !== deliveryId) {
      throw Object.assign(new Error('generation_delivery_conflict'), { code: 'generation_delivery_conflict' })
    }
    const replayedJob = { ...job }
    delete replayedJob.canvasVersion
    if (Object.hasOwn(job, 'canvasVersion')) await jobsStore.put(replayedJob)
    await tx.done
    return {
      replayed: true,
      localRevision: Number(job.localRevision) || Number(working?.localRevision) || 0,
      deliveryId,
      job: replayedJob,
    }
  }

  const localRevision = nextRevision(working)
  // Merge the pure renderer candidate over the last durable document before
  // adding this delivery.
  const document = normalizeDocument(input.canvasId, input.document, working?.document)
  const itemIds = generationItemIds(input, job)
  const importedAssets = generationImportedAssets(input, job, itemIds)
  // Fail closed on an EMPTY prepared-asset resolution too: a malformed job that
  // resolves zero assets must not commit items whose asset:// bytes were never
  // made durable (review W5).
  if (!importedAssets.length || importedAssets.length !== itemIds.length) {
    throw Object.assign(new Error('generation_result_item_count_mismatch'), { code: 'generation_result_item_count_mismatch' })
  }
  const documentItemIds = new Set((document?.items || []).map(item => String(item?.id || '')).filter(Boolean))
  if (itemIds.some(id => !documentItemIds.has(id))) {
    throw Object.assign(new Error('generation_result_item_missing'), { code: 'generation_result_item_missing' })
  }
  const baseVersion = Math.max(
    Number(input.baseVersion) || 0,
    Number(working?.version) || 0,
    Number(outbox?.baseVersion) || 0,
  )
  const nextJob = {
    ...job, clientJobId, status: 'delivered_local', deliveryId,
    localRevision, deliveredLocalAt: now,
    itemIds,
    importedAssets: importedAssets.length ? importedAssets : job.importedAssets,
    targetItemId: input.targetItemId || job.targetItemId || null,
  }
  delete nextJob.canvasVersion
  const durableResults = importedAssets.map(asset => ({
    ...asset,
    hash: assetHashFromUri(asset.uri),
    durableAsset: asset.assetRow,
  }))
  for (const result of durableResults) {
    if (!result.hash || !result.itemId || !result.durableAsset
      || String(result.durableAsset.hash).toLowerCase() !== result.hash
      || !(result.durableAsset.localBlob instanceof Blob || result.durableAsset.localBytes instanceof ArrayBuffer)) {
      throw Object.assign(new Error('generation_asset_not_durable'), { code: 'generation_asset_not_durable' })
    }
  }
  const nextWorking = {
    ...(working || {}), id: input.canvasId, document, version: baseVersion,
    localRevision, updatedAt: now,
  }
  const nextOutbox = {
    ...(outbox || {}), canvasId: input.canvasId, document, baseVersion,
    status: outbox?.status === 'conflict' ? 'conflict' : 'pending',
    attempts: Number(outbox?.attempts) || 0,
    localRevision, deliveryId, updatedAt: now,
  }

  const writes = [
    workingStore.put(nextWorking),
    outboxStore.put(nextOutbox),
    jobsStore.put(nextJob),
  ]
  for (const result of durableResults) writes.push(uploadStore.put(result.durableAsset))
  await Promise.all(writes)
  await tx.done
  return {
    replayed: false,
    localRevision,
    deliveryId,
    job: nextJob,
  }
}
